import { API_BASE_URL } from "../constants";
import { ReportCard, SolutionView } from "../types";

/**
 * Must stay in sync with MAX_UPLOAD_BYTES in
 * server/src/scenarios/gcs.service.ts — the server signs this exact range into
 * the upload URL, so a mismatch turns an oversized file into an opaque GCS 403.
 */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const ALLOWED_ARTIFACT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
];

export const ARTIFACT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
};

/**
 * The browser-reported MIME type, falling back to the filename extension when
 * the browser didn't supply one — some platforms report an empty `file.type`
 * for a perfectly genuine PDF or image, and telling that learner to "attach a
 * PDF or an image" is telling them to attach the thing they just attached.
 * Undefined only when neither source yields an answer.
 */
function inferContentType(file: File): string | undefined {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  return ext ? EXTENSION_CONTENT_TYPES[ext] : undefined;
}

function formatMiB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function jsonOrThrow<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    let detail = "";
    try {
      const body = await response.json();
      detail =
        typeof body?.message === "string"
          ? `: ${body.message}`
          : Array.isArray(body?.message)
            ? `: ${body.message.join(", ")}`
            : "";
    } catch {
      /* body wasn't JSON */
    }
    throw new Error(`${action} failed: ${response.status}${detail}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

interface SignUploadResponse {
  objectKey: string;
  signedUrl: string;
  expiresAt: number;
}

/**
 * Sign, then PUT the bytes straight to GCS. Returns the object key to submit
 * alongside the answer.
 */
export async function signSolutionUpload(
  sessionId: string,
  file: File,
  token?: string,
): Promise<string> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `That file is ${formatMiB(file.size)} MiB, which is larger than the 10 MiB limit. Attach a smaller one.`,
    );
  }
  const contentType = inferContentType(file);
  if (!contentType) {
    throw new Error(
      "Couldn't tell what kind of file this is from its name or contents. Rename it with a .pdf, .png, .jpg, or .webp extension and try again.",
    );
  }
  if (!ALLOWED_ARTIFACT_TYPES.includes(contentType)) {
    throw new Error(
      `Unsupported file type "${contentType}". Attach a PDF or an image (PNG, JPEG, or WebP).`,
    );
  }

  const res = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/solution/uploads/sign`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ filename: file.name, contentType }),
    },
  );
  const { objectKey, signedUrl } = await jsonOrThrow<SignUploadResponse>(
    res,
    "Sign upload",
  );

  // The server signs both Content-Type and x-goog-content-length-range into the
  // URL, so both headers must be sent with values matching the signature.
  const put = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
      "x-goog-content-length-range": `0,${MAX_UPLOAD_BYTES}`,
    },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed: ${put.status}`);

  return objectKey;
}

export async function submitSolution(
  sessionId: string,
  payload: { answer?: string; artifact?: string },
  token?: string,
): Promise<ReportCard> {
  const res = await fetch(`${API_BASE_URL}/sessions/${sessionId}/report-card`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<ReportCard>(res, "Submit answer");
}

/** Null when the session has no report card yet — a normal state, not an error. */
export async function getReportCard(
  sessionId: string,
  token?: string,
): Promise<ReportCard | null> {
  const res = await fetch(`${API_BASE_URL}/sessions/${sessionId}/report-card`, {
    headers: authHeaders(token),
  });
  if (res.status === 404) return null;
  return jsonOrThrow<ReportCard>(res, "Load report card");
}

/** Null when nothing has been submitted for this session yet. */
export async function getSolution(
  sessionId: string,
  token?: string,
): Promise<SolutionView | null> {
  const res = await fetch(`${API_BASE_URL}/sessions/${sessionId}/solution`, {
    headers: authHeaders(token),
  });
  if (res.status === 404) return null;
  return jsonOrThrow<SolutionView>(res, "Load solution");
}
